import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import {
  NameInitials,
  NumberRound,
  USDC,
  USDT,
  dateDifferenceInDaysFromToday,
  formatUSD
} from '../../helpers/helperFunctions';

import { useWindowWide } from '../../helpers/wideScreen';
import { CaptionText, H2, H3, H4, P2, P3 } from '../../styles/FontStyles';
import {
  Box,
  // Button,
  Flexed,
  ProfileImage,
  ProfileImagePlaceholder,
  ProfilePicture,
  UserImageCircle
} from '../../styles/GlobalComponents';
import { color, colors } from '../../styles/GlobalStyles';
import { Icon, IconCashBackBonus, IconPlus } from '../../styles/Icons';
import { Flex } from '../../styles/stylesStyled';

import { useAppSelector } from '../../app/hooks';
import { EARNINGS, SETTINGS_ACCOUNT } from '../../routes/routes';
import { Office } from '../../styles/svg';
import { User } from '../../types/loginTypes';
import { DashboardInformation } from './Dashboard.types';

import earningsUnderlay from '../../assets/images/earningsBack.png';
interface EarningsComponentProps {
  user: User;
  backOffice: DashboardInformation | null;
}

export const EarningsComponent = ({
  user,
  backOffice
}: EarningsComponentProps) => {
  const { t } = useTranslation('dashboard');
  const history = useHistory();
  const { dashboardInfo } = useAppSelector((state) => state.dashboard);
  const wide = useWindowWide(800);

  const HubInfo = () => {
    return (
      <Flexed row rowSM alignCenter gap='30px' gapSM='10px' width='100%'>
        <Flexed
          width='50%'
          widthSM='100%'
          style={{ cursor: 'pointer' }}
          center
          onClick={() => history.push('/backoffice/settings/virtual-office')}>
          <Icon color={colors.secondary100} mr='15' fill>
            <Office />
          </Icon>
          <Flexed column>
            <P3 bold> {t('backoffice_subscription')} </P3>

            <Flexed between>
              <CaptionText color={colors.neutral700}>
                {t('days_left')}:{' '}
                {`${dateDifferenceInDaysFromToday(
                  dashboardInfo?.user?.currentVirtualOfficeLicence?.dateEnd
                )}`}
              </CaptionText>

              <Icon color={colors.secondary100} fill>
                <IconPlus size={'14'} />
              </Icon>
            </Flexed>
          </Flexed>
        </Flexed>

        <Flexed
          width='50%'
          widthSM='100%'
          style={{ cursor: 'pointer' }}
          center
          onClick={() => history.push('/backoffice/cashback-bonus')}>
          <Icon color={colors.secondary100} mr='15' fill>
            <IconCashBackBonus />
          </Icon>

          <Flexed column>
            <P3 bold>{t('fast_start_bonus')}</P3>

            <Flexed between>
              <CaptionText color={colors.neutral700}>
                {dashboardInfo?.user?.fastStartBonus?.isClaimed
                  ? t('claimed')
                  : t('not_claimed')}
              </CaptionText>
            </Flexed>
          </Flexed>
        </Flexed>
      </Flexed>
    );
  };

  return (
    <div>
      <Box
        bg={colors.primary900}
        p='10px 20px'
        pSM='10px 10px'
        br='10px 10px 0 0'
        border='none'
        style={{ margin: 0 }}>
        <Flexed
          center
          gap='10px'
          style={{ cursor: 'pointer' }}
          onClick={() => history.push(SETTINGS_ACCOUNT)}>
          <UserImageCircle
            image={user?.profileImageUrl}
            name={user?.name}
            surname={user?.surname}
            size='60px'
            fontSize='20px'
            color={color.colors.neutral0}
            fontColor={color.colors.primary900}
          />
          <H4 color={colors.neutral0}>Hi! {user?.name}</H4>
        </Flexed>
      </Box>
      <Box
        m='0'
        mSM='0'
        p='20px 24px'
        br='0 0 10px 10px'
        relative
        overflow='hidden'
        bg={`url(${earningsUnderlay})`}
        bgSize='cover'
        bgPosition='center'
        bgRepeat='no-repeat'>
        <Flexed column gap='30px' gapSM='20px'>
          <Flexed
            style={{ cursor: 'pointer' }}
            rowSM
            gap='30px'
            gapSM='20px'
            onClick={() => history.push(EARNINGS)}>
            <Flexed column width='50%' widthSM='100%'>
              <H4
                fontFamily='Nulshock'
                color={colors.primary900}
                mb='4'
                mbSM='2'>
                {t('earnings.total_earnings')}
              </H4>

              <Flexed center gap='5px'>
                <H2 bold>
                  {backOffice &&
                    formatUSD(
                      NumberRound(backOffice?.user?.totalEarnings)
                    )}{' '}
                </H2>
                <P2 style={{ fontSize: '10px', fontWeight: 'normal' }}>
                  {USDT}
                  <br /> {USDC}
                </P2>
              </Flexed>

              {backOffice && (
                <P3 mt='8' flex color={colors.neutral500}>
                  <P3 color={colors.secondary100} mr='5' bold>
                    {backOffice?.user?.currentWeekEarnings > -1 && '+'}
                    {formatUSD(
                      NumberRound(backOffice?.user?.currentWeekEarnings)
                    )}{' '}
                    {USDT}{' '}
                  </P3>
                  {t('earnings.this_week')}
                </P3>
              )}
            </Flexed>
            <Flexed column width='50%' widthSM='100%'>
              <Flexed column mb='4'>
                <P2 semiBold>
                  {USDT}/{USDC} BALANCE
                </P2>
                <Flexed center end gap='5px'>
                  <H4 bold ml='40'>
                    {formatUSD(
                      backOffice?.user?.userTickets?.availableRewardPoints
                    )}
                  </H4>
                  <CaptionText
                    style={{ fontSize: '8px', fontWeight: 'normal' }}>
                    {USDT}
                    <br /> {USDC}
                  </CaptionText>
                </Flexed>
              </Flexed>
              <Flexed column mb='4'>
                <P2 semiBold>U1 BALANCE</P2>
                <Flexed center end gap='5px'>
                  <H4 bold ml='40'>
                    {formatUSD(backOffice?.user?.userTickets?.shoppingPoints)}
                  </H4>
                  <CaptionText
                    style={{ fontSize: '8px', fontWeight: 'normal' }}>
                    {USDT}
                    <br /> {USDC}
                  </CaptionText>
                </Flexed>
              </Flexed>
            </Flexed>
          </Flexed>
          <HubInfo />
        </Flexed>
      </Box>
    </div>
  );
};
